import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, BookOpen } from '@phosphor-icons/react';
import Layout from '../../components/layout/Layout';
import ChatMessage from '../../components/lessons/ChatMessage';
import { Message, DisplayItem } from '../../types/lesson';
import { LessonHistoryEntry } from '../../types/history';
import { lessonAPI } from '../../utils/api';

// API response interfaces for the lesson details endpoint
interface TutorResponse {
  text_content: string;
  spoken_content: string;
  display_content: DisplayContent[];
  subject: string;
}

interface DisplayContent {
  content: string;
  content_type: "latex" | "text" | "code" | "chemistry" | "description";
  render_method: "katex" | "plaintext" | "highlight" | "description";
  subject?: string;
  description?: string;
  display_timing?: number;
  display_duration?: number | null;
  metadata?: {
    language?: string;
    formula_type?: string;
    tool_suggestion?: string;
  };
}

interface LessonInteraction {
  timestamp: string;
  user_message: string;
  tutor_response: TutorResponse;
  image_filename?: string;
  ocr_success?: boolean;
  extracted_text?: string;
}

interface LessonDetailsResponse extends LessonHistoryEntry {
  interactions: LessonInteraction[];
}

export default function LessonHistoryViewPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [lessonDetails, setLessonDetails] = useState<LessonDetailsResponse | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Convert API interactions to Message format
  const convertInteractionsToMessages = (interactions: LessonInteraction[]): Message[] => {
    const messages: Message[] = [];
    let messageId = 1;

    interactions.forEach((interaction) => {
      // Skip LESSON_START messages as they're not really from the user
      if (interaction.user_message !== 'LESSON_START') {
        // Add user message
        messages.push({
          id: `${messageId++}`,
          content: interaction.user_message,
          sender: {
            id: 'student',
            name: 'You',
            role: 'student'
          },
          timestamp: new Date(interaction.timestamp),
          // Handle image if present
          ...(interaction.image_filename && {
            image: {
              file: new File([], interaction.image_filename),
              preview: interaction.image_filename
            }
          })
        });
      }

      // Add tutor response
      const displayItems: DisplayItem[] = interaction.tutor_response.display_content.map((content) => ({
        ...content,
        subject: content.subject || interaction.tutor_response.subject
      }));

      messages.push({
        id: `${messageId++}`,
        content: interaction.tutor_response.text_content,
        sender: {
          id: 'tutor',
          name: 'AI Tutor',
          role: 'tutor'
        },
        timestamp: new Date(interaction.timestamp),
        displayItems: displayItems.length > 0 ? displayItems : undefined
      });
    });

    return messages;
  };

  useEffect(() => {
    const fetchLessonData = async () => {
      if (!lessonId) {
        setError('No lesson ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data: LessonDetailsResponse = await lessonAPI.getLessonById(lessonId);

        setLessonDetails(data);
        
        // Convert interactions to messages
        if (data.interactions) {
          const convertedMessages = convertInteractionsToMessages(data.interactions);
          setMessages(convertedMessages);
        }
      } catch (err) {
        console.error('Failed to fetch lesson details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };

    fetchLessonData();
  }, [lessonId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const calculateDuration = (start: string, end: string | null) => {
    if (!end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    const minutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-1 justify-center items-center">
          <div className="text-white text-lg">Loading lesson...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="gap-1 px-6 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[920px] flex-1">
            <div className="flex items-center gap-4 px-4 pb-3 pt-5">
              <Link
                to="/history"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-border hover:bg-border-light transition-colors"
              >
                <ArrowLeft size={20} className="text-white" />
              </Link>
              <h2 className="text-white tracking-light text-[28px] font-bold leading-tight">
                Lesson History
              </h2>
            </div>
            
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="text-red-400 text-base">Failed to load lesson: {error}</div>
              <button
                onClick={() => navigate('/history')}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded h-10 px-4 bg-border text-white text-sm font-medium leading-normal"
              >
                Back to History
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="gap-1 px-6 flex flex-1 justify-center py-5">
        <div className="layout-content-container flex flex-col max-w-[920px] flex-1">
          {/* Header */}
          <div className="flex items-center gap-4 px-4 pb-3 pt-5">
            <Link
              to="/history"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-border hover:bg-border-light transition-colors"
            >
              <ArrowLeft size={20} className="text-white" />
            </Link>
            <h2 className="text-white tracking-light text-[28px] font-bold leading-tight">
              Lesson History
            </h2>
          </div>

          {/* Lesson Details Header */}
          {lessonDetails && (
            <div className="px-4 py-3 border-b border-border-light mb-4">
              <div className="flex items-start gap-4">
                <div className="text-white flex items-center justify-center rounded bg-border shrink-0 size-12">
                  <BookOpen size={24} weight="regular" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white text-xl font-semibold mb-1">
                    {lessonDetails.summary || `${lessonDetails.subject} - ${lessonDetails.topic || 'Lesson'}`}
                  </h3>
                  <div className="text-text-secondary text-sm space-y-1">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <BookOpen size={16} />
                        {lessonDetails.subject} • {lessonDetails.course}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar size={16} />
                        {formatDate(lessonDetails.started_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        Started at {formatTime(lessonDetails.started_at)}
                        {lessonDetails.ended_at && (
                          <span> • Duration: {calculateDuration(lessonDetails.started_at, lessonDetails.ended_at)}</span>
                        )}
                      </span>
                    </div>
                    {lessonDetails.interaction_count > 0 && (
                      <div className="text-text-secondary text-sm">
                        {lessonDetails.interaction_count} interactions
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Assessment Summary */}
              {lessonDetails.final_assessment && (
                <div className="mt-4 p-4 bg-input-bg rounded border border-border-light">
                  <h4 className="text-white text-sm font-medium mb-2">Final Assessment</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-text-secondary">Confidence:</span>
                      <span className={`capitalize font-medium ${
                        lessonDetails.final_assessment.confidence === 'high' ? 'text-green-400' :
                        lessonDetails.final_assessment.confidence === 'medium' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {lessonDetails.final_assessment.confidence}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-secondary">Level:</span>
                      <span className="text-white capitalize">{lessonDetails.final_assessment.new_level}</span>
                    </div>
                    {lessonDetails.final_assessment.recommendations.length > 0 && (
                      <div>
                        <span className="text-text-secondary">Recommendations:</span>
                        <ul className="mt-1 ml-4 list-disc list-inside text-text-secondary">
                          {lessonDetails.final_assessment.recommendations.map((rec, idx) => (
                            <li key={idx} className="text-sm">{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chat Messages Area - Using same layout as LessonPage */}
          <div className="flex-1 overflow-y-auto px-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-text-secondary text-base">
                  No conversation history available for this lesson.
                </div>
              </div>
            ) : (
              <>
                {/* Using the same ChatMessage component as LessonPage */}
                {messages.map((message) => {
                    console.log('Rendering message:', message);
                    return (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isCurrentUser={message.sender.role === 'student'}
                    isLessonHistory={true}
                  />
                )})}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}